import { ToastVariants } from '@features/ui-atoms/components/Toast';
import { showToast } from '@features/ui-atoms/services/showToast';
import { ENTITIES, Entity } from '@root/src/entities/enum';
import { memberActionMessages } from '@root/src/entities/Member/messages';
import { spaceObjectActionMessages } from '@root/src/entities/SpaceObject/messages';
import { sstActionMessages } from '@root/src/entities/Sst/messages';
import { userActionMessages } from '@root/src/entities/User/messages';
import { dedicatedForumActionMessages } from '@root/src/entities/DedicatedForum/messages';
import { routePath } from '@root/src/services/routeList';
import { Action } from '@root/src/types/common';
import FetchError from '@services/api/utils/FetchError';
import { Mutation, Query, QueryKey } from '@tanstack/react-query';
import { normalizeApiError, showErrorToast, STATUS_CODES } from '@utils/errors';
import { TFunction } from 'i18next';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { destroyAuthCookie, cleanupNonEssentialCookies } from './authCookie';

const actionMessagesMap = {
  [ENTITIES.MEMBER]: memberActionMessages,
  [ENTITIES.SPACE_OBJECT]: spaceObjectActionMessages,
  [ENTITIES.SST]: sstActionMessages,
  [ENTITIES.USER]: userActionMessages,
  [ENTITIES.DEDICATED_FORUM]: dedicatedForumActionMessages,
};

type GenericActionMessage = {
  successTitleKey?: string;
  successDescriptionKey?: string;
  formatMessage?: (data: unknown) => Record<string, string>;
};

const isLikelyTranslationKey = (value: string): boolean => value.includes('.') && !value.includes(' ');
const resolveInjectedTranslationKeys = (
  injectedData: Record<string, string> | undefined,
  t: TFunction<'translation', undefined>,
): Record<string, string> | undefined => {
  if (!injectedData) {
    return undefined;
  }

  return Object.entries(injectedData).reduce<Record<string, string>>((acc, [key, value]) => {
    if (!isLikelyTranslationKey(value)) {
      acc[key] = value;
      return acc;
    }
    const translatedValue = t(value);
    acc[key] = translatedValue !== value ? translatedValue : value;
    return acc;
  }, {});
};

const isInvalidTokenError = (error: FetchError): boolean => {
  const { code } = normalizeApiError(error);
  return code === 'error.auth.invalidToken';
};

export const handleGenericMutationErrors = (
  error: FetchError,
  variables: unknown,
  context: unknown,
  mutation: Mutation<unknown, unknown, unknown, unknown>,
  router: AppRouterInstance,
  t: TFunction<'translation', undefined>,
): void => {
  const status = error?.response?.status;

  // Redirecting to login: don't show an error toast on 401
  if (status === 401) {
    handleHTTPStatus(status, router);
    return;
  }

  // Handle invalid token errors gracefully: redirect without showing error toast
  if (isInvalidTokenError(error)) {
    if (status === STATUS_CODES.FORBIDDEN) {
      destroyAuthCookie();
      router.push(routePath.login);
    } else if (status) {
      handleHTTPStatus(status, router);
    }
    return;
  }

  if (mutation.meta?.skipSuccessToast) {
    if (status) {
      handleHTTPStatus(status, router);
    }
    return;
  }

  // Check for privacy mode related issues
  if (
    error.message &&
    (error.message.toLowerCase().includes('cors') ||
      error.message.toLowerCase().includes('credentials') ||
      error.message.toLowerCase().includes('cookie') ||
      error.message.toLowerCase().includes('privacy'))
  ) {
    showToast({
      variant: ToastVariants.ERROR,
      title: t('api.error'),
      message: t('api.error.privacyMode'),
    });
  } else {
    showErrorToast(error, t);
  }

  if (status) {
    handleHTTPStatus(status, router);
  } else {
    // console.error('Error encountered without a status code:', error);
  }
};

export const handleGenericQueryErrors = (
  error: FetchError,
  query: Query<unknown, unknown, unknown, QueryKey>,
  router: AppRouterInstance,
  t: TFunction<'translation', undefined>,
): void => {
  const status = error?.response?.status;

  // Redirecting to login: don't show an error toast on 401
  if (status === 401) {
    handleHTTPStatus(status, router);
    return;
  }

  // Handle invalid token errors gracefully: redirect without showing error toast
  if (isInvalidTokenError(error)) {
    if (status === STATUS_CODES.FORBIDDEN) {
      destroyAuthCookie();
      router.push(routePath.login);
    } else if (status) {
      handleHTTPStatus(status, router);
    }
    return;
  }

  // Skip error toast for 404 errors on queries whose page handles a missing resource
  // (e.g. a deleted member in a byline: `memberQueries.detail`)
  if (status === STATUS_CODES.NOT_FOUND && query.meta?.skipErrorToastOn404) {
    return;
  }

  // Skip error toast if meta.skipErrorToast is set
  if (query.meta?.skipErrorToast) {
    if (status) {
      handleHTTPStatus(status, router);
    }
    return;
  }

  // Skip error toast for 403 on queries that handle it in-page (e.g. dedicated forum detail:
  // "Request Access" / pending state is shown instead of a global toast)
  if (status === STATUS_CODES.FORBIDDEN && query.meta?.skipLogoutOn403) {
    return;
  }

  // Show error toast — getErrorMessage handles code resolution, status fallback, and privacy mode
  showErrorToast(error, t);

  // Handle 403 errors - only logout if not explicitly skipped
  if (status === STATUS_CODES.FORBIDDEN) {
    // Skip logout for specific queries where 403 is expected (e.g., forum participants for closed forums)
    if (query.meta?.skipLogoutOn403) {
      // Don't logout, just return - the component will handle the error gracefully
      return;
    }
    destroyAuthCookie();
    router.push(routePath.login);
  }

  if (status) {
    handleHTTPStatus(status, router);
  } else {
    // console.error('Error encountered without a status code:', error);
  }
};

export const handleGenericMutationSuccess = (
  data: unknown,
  variables: unknown,
  context: unknown,
  mutation: Mutation<unknown, unknown, unknown, unknown>,
  t: TFunction<'translation', undefined>,
): void => {
  const { meta: { action, entity, skipSuccessToast } = {} } = mutation;

  if (skipSuccessToast) {
    return;
  }

  if (action && entity) {
    const typedAction = action as Action;
    const typedEntity = entity as Entity;
    const messages = actionMessagesMap[typedEntity];
    const actionMessages = messages[typedAction] as GenericActionMessage | undefined;
    const injectedData = resolveInjectedTranslationKeys(actionMessages?.formatMessage?.(data), t);

    if (actionMessages?.successTitleKey && actionMessages?.successDescriptionKey) {
      const toastTitle = t(actionMessages.successTitleKey, injectedData);
      const toastDescription = t(actionMessages.successDescriptionKey, injectedData);

      showToast({
        variant: ToastVariants.SUCCESS,
        title: toastTitle,
        message: toastDescription,
      });
    }
  }
};

export const handleHTTPStatus = (code: number, router: AppRouterInstance): void => {
  if (code === 401) {
    try {
      // Best-effort: ask Next API to expire the HttpOnly cookie
      void fetch('/api/auth/clear-cookie', { method: 'POST', credentials: 'include' });
    } catch {
      // ignore
    }
    // Clean any non-essential cookies that could mislead UI state
    cleanupNonEssentialCookies();
    destroyAuthCookie();
    router.push(routePath.login);
  }
};
